// src/pages/productions/ph01/DailyProductionData.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
    MenuItem,
    Tooltip
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, update, push, query, orderByChild, get } from "firebase/database";

import ProductionSummary from "../production-summary/ProductionSummary";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatDateDisplay,
    formatText,
    formatToTwoDecimalWithRound
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import fungsi import dari file utilitas baru
import { 
    importExcelToFirebaseDailyProduction, 
    onValuePromise 
} from "../utils/excelImportDailyProduction";

// Import fungsi perhitungan dari Calculation.js
import { calculateWeightValues } from "../formula/Calculation";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Helper function untuk parse string number dengan 4 digit desimal
const parseFourDecimalString = (str) => {
    if (!str) return 0;
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
};

// Konstanta untuk SLoc yang diizinkan (case-insensitive)
const ALLOWED_SLOCS = ['PH01'];
const ALLOWED_SLOCS_UPPERCASE = ALLOWED_SLOCS.map(sloc => sloc.toUpperCase());

// Helper function untuk memeriksa apakah SLoc diizinkan
const isAllowedSloc = (sloc) => {
    if (!sloc) return false;
    return ALLOWED_SLOCS_UPPERCASE.includes(sloc.toUpperCase().trim());
};

function DailyProductionData() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [locationData, setLocationData] = useState([]);
    const [masterData, setMasterData] = useState([]);
    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [loadingMasterData, setLoadingMasterData] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterMaterialCode, setFilterMaterialCode] = useState('');
    const [filterLocation, setFilterLocation] = useState('');
    const [filterDateRange, setFilterDateRange] = useState({ start: '', end: '' });

    // Fungsi untuk fetch data master dari Firebase
    const fetchMasterData = useCallback(async () => {
        setLoadingMasterData(true);
        try {
            console.log("📥 Fetching master data from Firebase...");
            const masterRef = ref(database, "db_productions/tb_master_data");
            const masterSnapshot = await onValuePromise(masterRef);
            const masterData = masterSnapshot.val();
            
            if (masterData) {
                const masterDataArray = Object.keys(masterData).map(key => ({
                    firebaseKey: key,
                    material_code: masterData[key].material_code || '',
                    spec_code: masterData[key].spec_code || '',
                    description: masterData[key].description || '',
                    class: masterData[key].class || '',
                    size: masterData[key].size || '',
                    berat_kemasan: masterData[key].berat_kemasan || ''
                }));
                
                console.log("✅ Data master berhasil di-fetch:", masterDataArray.length, "records");
                
                // Simpan dalam Map untuk akses cepat berdasarkan material_code
                const masterDataMap = {};
                masterDataArray.forEach(item => {
                    if (item.material_code) {
                        masterDataMap[item.material_code] = item;
                    }
                });
                
                setMasterData(masterDataArray);
                return masterDataMap;
            }
            console.log("⚠️ Tidak ada data master ditemukan");
            return {};
        } catch (error) {
            console.error("Error fetching master data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memuat data master.",
                severity: "error",
            });
            return {};
        } finally {
            setLoadingMasterData(false);
        }
    }, []);

    // Fungsi untuk mendapatkan data master berdasarkan material_code
    const getMasterDataByMaterialCode = useCallback((materialCode, masterDataMap) => {
        if (!materialCode || !masterDataMap || Object.keys(masterDataMap).length === 0) {
            return null;
        }
        
        return masterDataMap[materialCode] || null;
    }, []);

    // Fungsi untuk fetch data lokasi dari Firebase dengan cache
    const fetchLocationData = useCallback(async () => {
        setLoadingLocation(true);
        try {
            console.log("📥 Fetching location data from Firebase...");
            const locationRef = ref(database, "db_freshpine/tb_lokasi");
            const locationSnapshot = await onValuePromise(locationRef);
            const locationData = locationSnapshot.val();
            
            if (locationData) {
                const locations = Object.keys(locationData).map(key => ({
                    firebaseKey: key,
                    id_lokasi: locationData[key].id_lokasi || '',
                    lokasi: locationData[key].lokasi || '',
                    bibit: locationData[key].bibit || ''
                }));
                
                console.log("✅ Data lokasi berhasil di-fetch:", locations.length, "records");
                
                setLocationData(locations);
                return locations;
            }
            console.log("⚠️ Tidak ada data lokasi ditemukan");
            return [];
        } catch (error) {
            console.error("Error fetching location data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memuat data lokasi.",
                severity: "error",
            });
            return [];
        } finally {
            setLoadingLocation(false);
        }
    }, []);

    // Fungsi untuk mendapatkan lokasi berdasarkan id_lokasi - VERSI OPTIMASI
    const getLocationFromIdLokasi = useCallback((id_lokasi, locationData) => {
        if (!id_lokasi || id_lokasi === '' || !locationData || locationData.length === 0) {
            return '-';
        }
        
        const searchId = String(id_lokasi).trim();
        
        // Cari dengan exact match
        const foundLocation = locationData.find(loc => {
            const locId = String(loc.id_lokasi || '').trim();
            return locId === searchId;
        });
        
        if (foundLocation) {
            return foundLocation.lokasi || '-';
        }
        
        return '-';
    }, []);

    // Fungsi untuk mendapatkan variety - VERSI OPTIMASI
    const getVarietyFromIdLokasi = useCallback((id_lokasi, locationData) => {
        if (!id_lokasi || id_lokasi === '' || !locationData || locationData.length === 0) {
            return 'MD2';  // Default ke MD2 jika tidak ada id_lokasi atau data lokasi
        }
        
        const searchId = String(id_lokasi).trim();
        
        const foundLocation = locationData.find(loc => {
            const locId = String(loc.id_lokasi || '').trim();
            return locId === searchId;
        });
        
        if (!foundLocation || !foundLocation.bibit) {
            return 'MD2';  // Default ke MD2 jika tidak ada data bibit
        }
        
        const bibit = String(foundLocation.bibit).trim();
        
        // Cek jika bibit string kosong atau hanya whitespace
        if (bibit === '' || !bibit) {
            return 'MD2';  // Default ke MD2 jika bibit kosong
        }
        
        // Ambil karakter ke-3 sampai ke-5 (indeks 2-4)
        if (bibit.length >= 5) {
            return bibit.substring(2, 5);
        } else if (bibit.length >= 3) {
            return bibit.substring(2);
        }
        
        return 'MD2';  // Default ke MD2 jika panjang bibit kurang dari 3 karakter
    }, []);

    // Fungsi utama untuk fetch data dengan filter SLoc
    const fetchData = useCallback(async () => {
        console.log("DEBUG fetchData: Memulai proses fetch data dengan filter SLoc...");
        setLoadingData(true);
        try {
            // Fetch semua data yang diperlukan secara paralel untuk optimasi
            console.log("DEBUG fetchData: Fetch data lokasi, master, dan produksi...");
            
            const [locations, masterDataMap, productionSnapshot] = await Promise.all([
                fetchLocationData(),
                fetchMasterData(),
                onValuePromise(ref(database, "db_productions/tb_daily_production"))
            ]);
            
            const productionData = productionSnapshot.val();
            
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (productionData) {
                Object.keys(productionData).forEach(key => {
                    const record = productionData[key];
                    const sloc = record.sloc || '';
                    
                    // FILTER: Hanya ambil data dengan SLoc PH01 (case-insensitive)
                    if (isAllowedSloc(sloc)) {
                        loadedData.push({ 
                            firebaseKey: key, 
                            id_lokasi: record.id_lokasi || '',
                            sloc: sloc,
                            packing_date: record.packing_date || '',
                            material_code: record.material_code || '',
                            batch: record.batch || '',
                            sum_of_qty: record.sum_of_qty || '0.0000',
                            sum_of_weight: record.sum_of_weight || '0.0000',
                            last_imported_at: record.last_imported_at || null
                        });
                        
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    }
                });
                
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
                
                console.log("DEBUG fetchData: Data produksi PH01 yang diambil:", loadedData.length, "records");
                console.log("DEBUG fetchData: SLoc yang diizinkan:", ALLOWED_SLOCS);
            } else {
                console.log("DEBUG fetchData: Tidak ada data produksi ditemukan");
            }

            // Transform data untuk ditampilkan di UI
            const transformedData = loadedData.map(item => {
                const firebaseKey = item.firebaseKey;
                const id_lokasi = item.id_lokasi;
                const sloc = item.sloc;
                const packing_date = item.packing_date;
                const material_code = item.material_code;
                const batch = item.batch;
                const sum_of_qty = parseFourDecimalString(item.sum_of_qty);
                const sum_of_weight = parseFourDecimalString(item.sum_of_weight);
                
                // Cari data master berdasarkan material_code
                const masterDataItem = getMasterDataByMaterialCode(material_code, masterDataMap);
                
                // Gunakan data dari master jika ditemukan, jika tidak gunakan "-"
                const spec_code = masterDataItem ? masterDataItem.spec_code : '-';
                const description = masterDataItem ? masterDataItem.description : '-';
                const location = getLocationFromIdLokasi(id_lokasi, locations);
                const variety = getVarietyFromIdLokasi(id_lokasi, locations);
                
                // Gunakan data grading dari master jika ada, jika tidak gunakan "-"
                const grade_class = masterDataItem ? masterDataItem.class : '-';
                const size = masterDataItem ? masterDataItem.size : '-';
                
                // Gunakan berat_kemasan dari master jika ada, jika tidak gunakan "0.0000"
                const berat_kemasan = masterDataItem ? masterDataItem.berat_kemasan : '0.0000';
                
                const sum_of_weight_display = formatToTwoDecimalWithRound(item.sum_of_weight);
                
                // Format packing date untuk display jika perlu
                const formatted_packing_date = packing_date;

                // Siapkan data untuk perhitungan
                const calculationData = {
                    sum_of_qty: item.sum_of_qty, // String dari database
                    sum_of_weight: item.sum_of_weight, // String dari database
                    berat_kemasan
                };

                // Hitung berat box dan netto
                const calculatedData = calculateWeightValues(calculationData);

                return {
                    firebaseKey,
                    sloc,
                    packing_date: formatted_packing_date,
                    spec_code,
                    material_code,
                    description,
                    location,
                    variety,
                    batch,
                    id_lokasi,
                    grade_class,
                    size,
                    sum_of_qty,
                    sum_of_qty_string: item.sum_of_qty,
                    sum_of_weight,
                    sum_of_weight_string: item.sum_of_weight,
                    sum_of_weight_display,
                    netto: calculatedData.netto,
                    berat_kemasan,
                    berat_box: calculatedData.berat_box,
                    last_imported_at: item.last_imported_at,
                    hasMasterData: !!masterDataItem
                };
            });
            
            console.log("DEBUG fetchData: Data setelah transform:", transformedData.length, "records");
            console.log("DEBUG fetchData: Data dengan master data:", transformedData.filter(item => item.hasMasterData).length, "records");
            
            setOriginalDataList(transformedData);
            setDataList(transformedData);

        } catch (error) {
            console.error("Error fetching data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memuat data.",
                severity: "error",
            });
        } finally {
            setLoadingData(false);
            console.log("DEBUG fetchData: Proses fetch data selesai");
        }
    }, [fetchLocationData, fetchMasterData, getLocationFromIdLokasi, getVarietyFromIdLokasi, getMasterDataByMaterialCode]);

    // Effect untuk fetch data awal
    useEffect(() => {
        if (user) {
            fetchData();
        }
    }, [user, fetchData]);

    // Fungsi untuk refresh data
    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await fetchData();
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui",
                severity: "success",
            });
        } catch (error) {
            console.error("Gagal refresh data:", error);
        } finally {
            setRefreshing(false);
        }
    }, [fetchData]);

    // Fungsi untuk refresh data setelah import
    const refreshProductionData = useCallback(async () => {
        console.log("DEBUG refreshProductionData: Memulai refresh data dengan filter SLoc...");
        
        const [locations, masterDataMap] = await Promise.all([
            fetchLocationData(),
            fetchMasterData()
        ]);
        
        const productionRef = ref(database, "db_productions/tb_daily_production");
        const updatedProductionSnapshot = await onValuePromise(productionRef);
        const updatedProductionData = updatedProductionSnapshot.val() || {};
        
        const updatedLoadedData = [];
        Object.keys(updatedProductionData).forEach(key => {
            const record = updatedProductionData[key];
            const sloc = record.sloc || '';
            
            // FILTER: Hanya ambil data dengan SLoc PH01 (case-insensitive)
            if (isAllowedSloc(sloc)) {
                updatedLoadedData.push({ 
                    firebaseKey: key, 
                    id_lokasi: record.id_lokasi || '',
                    sloc: sloc,
                    packing_date: record.packing_date || '',
                    material_code: record.material_code || '',
                    batch: record.batch || '',
                    sum_of_qty: record.sum_of_qty || '0.0000',
                    sum_of_weight: record.sum_of_weight || '0.0000',
                    last_imported_at: record.last_imported_at || null
                });
            }
        });
        
        // Transform data untuk UI
        const finalData = updatedLoadedData.map(item => {
            const sum_of_qty = parseFourDecimalString(item.sum_of_qty);
            const sum_of_weight = parseFourDecimalString(item.sum_of_weight);
            
            // Cari data master berdasarkan material_code
            const masterDataItem = getMasterDataByMaterialCode(item.material_code, masterDataMap);
            
            // Gunakan data dari master jika ditemukan, jika tidak gunakan "-"
            const spec_code = masterDataItem ? masterDataItem.spec_code : '-';
            const description = masterDataItem ? masterDataItem.description : '-';
            const location = getLocationFromIdLokasi(item.id_lokasi, locations);
            const variety = getVarietyFromIdLokasi(item.id_lokasi, locations);
            
            // Gunakan data grading dari master jika ada, jika tidak gunakan "-"
            const grade_class = masterDataItem ? masterDataItem.class : '-';
            const size = masterDataItem ? masterDataItem.size : '-';
            
            // Gunakan berat_kemasan dari master jika ada, jika tidak gunakan "0.0000"
            const berat_kemasan = masterDataItem ? masterDataItem.berat_kemasan : '0.0000';
            
            const sum_of_weight_display = formatToTwoDecimalWithRound(item.sum_of_weight);
            
            // Siapkan data untuk perhitungan
            const calculationData = {
                sum_of_qty: item.sum_of_qty,
                sum_of_weight: item.sum_of_weight,
                berat_kemasan
            };

            // Hitung berat box dan netto
            const calculatedData = calculateWeightValues(calculationData);
            
            return {
                firebaseKey: item.firebaseKey,
                sloc: item.sloc,
                packing_date: item.packing_date,
                spec_code,
                material_code: item.material_code,
                description,
                location,
                variety,
                batch: item.batch,
                id_lokasi: item.id_lokasi,
                grade_class,
                size,
                sum_of_qty,
                sum_of_qty_string: item.sum_of_qty,
                sum_of_weight,
                sum_of_weight_string: item.sum_of_weight,
                sum_of_weight_display,
                netto: calculatedData.netto,
                berat_kemasan,
                berat_box: calculatedData.berat_box,
                last_imported_at: item.last_imported_at,
                hasMasterData: !!masterDataItem
            };
        });
        
        console.log("DEBUG refreshProductionData: Data final setelah transform:", finalData.length, "records");
        
        return finalData;
    }, [fetchLocationData, fetchMasterData, getLocationFromIdLokasi, getVarietyFromIdLokasi, getMasterDataByMaterialCode]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const queryLower = searchQuery.toLowerCase();
            const filtered = originalDataList.filter(item => 
                (item.location && item.location.toLowerCase().includes(queryLower)) ||
                (item.material_code && item.material_code.toLowerCase().includes(queryLower)) ||
                (item.batch && item.batch.toLowerCase().includes(queryLower)) ||
                (item.id_lokasi && item.id_lokasi.toLowerCase().includes(queryLower)) ||
                (item.description && item.description.toLowerCase().includes(queryLower)) ||
                (item.variety && item.variety.toLowerCase().includes(queryLower))
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handler untuk pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            const result = await importExcelToFirebaseDailyProduction(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui.`,
                    severity: "success",
                });
                
                setLastUpdated(new Date());
                
                // Refresh data dengan filter SLoc
                const updatedData = await refreshProductionData();
                
                setDataList(updatedData);
                setOriginalDataList(updatedData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            setImportLoading(false);
        }
    };

    const handleExport = async () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export sedang dikembangkan...",
            severity: "info",
        });
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print sedang dikembangan...",
            severity: "info",
        });
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterMaterialCode) {
            filtered = filtered.filter(item => item.material_code === filterMaterialCode);
        }
        if (filterLocation) {
            filtered = filtered.filter(item => item.location === filterLocation);
        }
        if (filterDateRange.start) {
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.packing_date.split('-').reverse().join('-'));
                const startDate = new Date(filterDateRange.start);
                return itemDate >= startDate;
            });
        }
        if (filterDateRange.end) {
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.packing_date.split('-').reverse().join('-'));
                const endDate = new Date(filterDateRange.end);
                return itemDate <= endDate;
            });
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterMaterialCode('');
        setFilterLocation('');
        setFilterDateRange({ start: '', end: '' });
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Get unique values for filter dropdowns menggunakan useMemo untuk optimasi
    const { materialCodeList, locationList } = useMemo(() => {
        const materialCodes = [...new Set(originalDataList.map(item => item.material_code))].filter(Boolean).sort();
        const locations = [...new Set(originalDataList.map(item => item.location))].filter(Boolean).sort();
        return { materialCodeList: materialCodes, locationList: locations };
    }, [originalDataList]);

    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                    Memeriksa autentikasi...
                </Typography>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedData = displayedDataList.slice(startIndex, endIndex);

    return (
        <Box sx={{ pb: 3 }}>
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/* <ProductionSummary/>*/}

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterAltOutlinedIcon /> Filter Data Produksi Harian PH01
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Material Code</InputLabel>
                            <Select
                                value={filterMaterialCode}
                                label="Material Code"
                                onChange={(e) => setFilterMaterialCode(e.target.value)}
                            >
                                <MenuItem value=""><em>Semua</em></MenuItem>
                                {materialCodeList.map((material) => (
                                    <MenuItem key={material} value={material}>{material}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Location</InputLabel>
                            <Select
                                value={filterLocation}
                                label="Location"
                                onChange={(e) => setFilterLocation(e.target.value)}
                            >
                                <MenuItem value=""><em>Semua</em></MenuItem>
                                {locationList.map((location) => (
                                    <MenuItem key={location} value={location}>{location}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Tanggal Mulai"
                            type="date"
                            value={filterDateRange.start}
                            onChange={(e) => setFilterDateRange(prev => ({ ...prev, start: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Tanggal Akhir"
                            type="date"
                            value={filterDateRange.end}
                            onChange={(e) => setFilterDateRange(prev => ({ ...prev, end: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>Reset Filter</Button>
                        <Button variant="contained" onClick={handleFilterData}>Terapkan Filter</Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h6" component="h2">
                            Data Produksi Harian PH01
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari lokasi, material, batch..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 300 }}
                        />
                        <Tooltip title="Refresh Data">
                            <IconButton color="primary" size="small" onClick={handleRefresh} disabled={refreshing}>
                                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Filter">
                            <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                                {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Import Excel">
                            <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                                {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                            </IconButton>
                        </Tooltip>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <Tooltip title="Export Data">
                            <IconButton color="primary" size="small" onClick={handleExport}>
                                <FileDownloadOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                            <IconButton color="primary" size="small" onClick={handlePrint}>
                                <PrintOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {loadingData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Memuat data produksi PH01...</Typography>
                    </Box>
                ) : displayedDataList.length > 0 ? (
                    <>
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="daily production table" size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={2} className="ded-tch" sx={{ width: 60 }}>No</TableCell>
                                        <TableCell colSpan={2} className="ded-tch">Packing Info</TableCell>
                                        <TableCell colSpan={3} className="ded-tch">Product Identity</TableCell>
                                        <TableCell colSpan={3} className="ded-tch">Harvest Info</TableCell>
                                        <TableCell colSpan={2} className="ded-tch">Grading Info</TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">Sum of Qty</TableCell>
                                        <TableCell colSpan={2} className="ded-tch">Weight</TableCell>
                                        <TableCell colSpan={2} className="ded-tch">Packaging</TableCell>
                                    </TableRow>
                                    
                                    <TableRow className="tr">
                                        <TableCell className="ded-tc">SLoc</TableCell>
                                        <TableCell className="ded-tc">Packing Date</TableCell>
                                        <TableCell className="ded-tc">Spec Code</TableCell>
                                        <TableCell className="ded-tc">Material Code</TableCell>
                                        <TableCell className="ded-tc">Description</TableCell>
                                        <TableCell className="ded-tc">Location</TableCell>
                                        <TableCell className="ded-tc">Variety</TableCell>
                                        <TableCell className="ded-tc">Batch</TableCell>
                                        <TableCell className="ded-tc">Class</TableCell>
                                        <TableCell className="ded-tc">Size</TableCell>
                                        <TableCell className="ded-tc">Sum of Weight (KG)</TableCell>
                                        <TableCell className="ded-tc">Netto (KG)</TableCell>
                                        <TableCell className="ded-tc">Berat Kemasan (KG)</TableCell>
                                        <TableCell className="ded-tc">Berat Box (KG)</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginatedData.map((row, index) => {
                                        const rowNumber = startIndex + index + 1;
                                        
                                        return (
                                            <TableRow key={row.firebaseKey || index} hover>
                                                <TableCell>{rowNumber}</TableCell>
                                                <TableCell>{formatText(row.sloc)}</TableCell>
                                                <TableCell>{formatText(row.packing_date)}</TableCell>
                                                {/* Spec Code - Ambil dari master data */}
                                                <TableCell>{formatText(row.spec_code)}</TableCell>
                                                <TableCell className="ded-tbtrtc">{formatText(row.material_code)}</TableCell>
                                                {/* Description - Ambil dari master data */}
                                                <TableCell className="ded-tbtrtc">{formatText(row.description)}</TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                            {formatText(row.location)}
                                                        </div>
                                                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                            ID: {row.id_lokasi || '-'}
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{formatText(row.variety)}</TableCell>
                                                <TableCell>{formatText(row.batch)}</TableCell>
                                                {/* Class - Ambil dari master data */}
                                                <TableCell>
                                                    {formatText(row.grade_class)}
                                                </TableCell>
                                                {/* Size - Ambil dari master data */}
                                                <TableCell>
                                                    {formatText(row.size)}
                                                </TableCell>
                                                <TableCell>{formatText(row.sum_of_qty_string)}</TableCell>
                                                <TableCell>{row.sum_of_weight_display}</TableCell>
                                                {/* Netto (KG) - Hasil perhitungan */}
                                                <TableCell>
                                                    {formatText(row.netto)}
                                                </TableCell>
                                                {/* Berat Kemasan - Ambil dari master data */}
                                                <TableCell>
                                                    {formatText(row.berat_kemasan)}
                                                </TableCell>
                                                {/* Berat Box (KG) - Hasil perhitungan */}
                                                <TableCell>
                                                    {formatText(row.berat_box)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 300,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1,
                        p: 3
                    }}>
                        {searchQuery ? (
                            <>
                                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Tidak ada data PH01 yang sesuai dengan pencarian
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Coba kata kunci lain atau reset filter
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    sx={{ mt: 2 }}
                                    onClick={() => setSearchQuery('')}
                                >
                                    Reset Pencarian
                                </Button>
                            </>
                        ) : (
                            <>
                                <FileUploadOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Tidak ada data produksi harian PH01
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Import data Excel untuk memulai
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    startIcon={<FileUploadOutlinedIcon />}
                                    sx={{ mt: 2 }}
                                    onClick={handleImportButtonClick}
                                >
                                    Import Data
                                </Button>
                            </>
                        )}
                    </Box>
                )}
            </Paper>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default DailyProductionData;